import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getImageUrl } from '../utils/cdnUtils'
import { useAuth } from '../contexts/AuthContext'
import { useWallet } from '../contexts/WalletContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useBankIcons } from '../contexts/BankContext'
import { userInfoService } from '../services/userInfoService'
import type { BankAccount } from '../contexts/UserInfoContext'

interface ProfilePageProps {
  themeConfig?: any
  assetConfig?: any
}

function formatRegisterDate(isoDate: string | undefined): string {
  if (!isoDate) return '-'
  try {
    const d = new Date(isoDate)
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })
  } catch {
    return isoDate
  }
}

const ProfilePage: React.FC<ProfilePageProps> = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { walletData, isLoadingWallet, formatCurrency } = useWallet()
  const { userInfo, isLoadingUserInfo, getPrimaryBankAccount, refreshUserInfo } = useUserInfo()
  const { getBankIcon, getAvailableBanks, isLoadingBankIcons } = useBankIcons()

  const [showAddBankModal, setShowAddBankModal] = useState(false)
  const [addBankSubmitting, setAddBankSubmitting] = useState(false)
  const [showBankDropdown, setShowBankDropdown] = useState(false)
  const [editBankCode, setEditBankCode] = useState('')
  const [editBankName, setEditBankName] = useState('')
  const [editAccountNumber, setEditAccountNumber] = useState('')
  const [editAccountName, setEditAccountName] = useState('')
  const bankDropdownRef = useRef<HTMLDivElement | null>(null)

  const primaryBank = getPrimaryBankAccount()
  const activeBankAccounts: BankAccount[] = userInfo?.bankAccount?.filter(
    (acc) => acc.isActive && !acc.isDelete
  ) ?? []
  const canAddBank = activeBankAccounts.length === 0
  const availableBanks = useMemo(
    () => getAvailableBanks().map((bank) => ({
      code: bank.abbreviation || bank.bankCode,
      name: bank.name,
      imageUrl: bank.imageUrl || getImageUrl('icons-default-bank.png'),
    })),
    [getAvailableBanks]
  )
  const selectedBank = useMemo(
    () => availableBanks.find((bank) => bank.code === editBankCode) || null,
    [availableBanks, editBankCode]
  )

  const handleOpenAddBankModal = useCallback(() => {
    if (!canAddBank) return
    setShowBankDropdown(false)
    setEditBankCode('')
    setEditBankName('')
    setEditAccountNumber('')
    setEditAccountName('')
    setShowAddBankModal(true)
  }, [canAddBank])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const shouldOpenAddBank = searchParams.get('openAddBank') === '1'

    if (!shouldOpenAddBank || !canAddBank) return

    handleOpenAddBankModal()
    toast('กรุณาเพิ่มบัญชีธนาคารให้เรียบร้อยก่อนทำรายการฝากเงิน', {
      icon: '🏦',
    })
    navigate('/profile', { replace: true })
  }, [location.search, canAddBank, handleOpenAddBankModal, navigate])

  useEffect(() => {
    if (!showBankDropdown) return

    const handleOutsideClick = (event: MouseEvent) => {
      if (!bankDropdownRef.current?.contains(event.target as Node)) {
        setShowBankDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [showBankDropdown])

  const handleSubmitAddBank = useCallback(async () => {
    const bankCode = editBankCode.trim()
    const bankName = editBankName.trim() || availableBanks.find((b) => b.code === bankCode)?.name || bankCode
    const accountNumber = editAccountNumber.replace(/\D/g, '')
    const accountName = editAccountName.trim()

    if (!bankCode) {
      toast.error('กรุณาเลือกธนาคาร')
      return
    }
    if (!accountNumber || accountNumber.length < 10 || accountNumber.length > 12) {
      toast.error('หมายเลขบัญชีต้อง 10–12 หลัก')
      return
    }
    if (!accountName) {
      toast.error('กรุณากรอกชื่อบัญชี')
      return
    }

    setAddBankSubmitting(true)
    const res = await userInfoService.updateUserInfo({
      bankAccount: [{ bankCode, bankName, accountNumber, accountName }],
    })
    setAddBankSubmitting(false)
    if (res.success) {
      setShowBankDropdown(false)
      setShowAddBankModal(false)
      toast.success('เพิ่มบัญชีธนาคารเรียบร้อยแล้ว')
      await refreshUserInfo()
    } else {
      toast.error(res.error || 'บันทึกไม่สำเร็จ')
    }
  }, [editBankCode, editBankName, editAccountNumber, editAccountName, refreshUserInfo, availableBanks])

  const userData = {
    username: userInfo?.username || user?.username || 'ผู้ใช้',
    fullName: userInfo?.name || `${(user as any)?.firstName || ''} ${(user as any)?.lastName || ''}`.trim() || user?.username || 'ผู้ใช้',
    balance: walletData ? formatCurrency(walletData.balance.main) : (isLoadingWallet ? 'กำลังโหลด...' : '0.00'),
    mobile: userInfo?.mobile || (user as any)?.mobile || (user as any)?.phoneNumber || '-',
    email: userInfo?.email || (user as any)?.email || '-',
    registerAt: formatRegisterDate(userInfo?.registerAt),
  }

  const getAccountIconUrl = (acc: BankAccount): string => {
    const icon = getBankIcon(acc.bankCode)
    return icon?.imageUrl || getImageUrl('icons-default-bank.png')
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <Link to="/dashboard" className="txt-link-d">
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </Link>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-cog me-2"></i>
                      ตั้งค่าบัญชีผู้ใช้
                    </div>

                    <div className="bdr-5 bgc-4 c-red p-2">
                      <div className="c-dark mb-2 t-medium">
                        <i className="fas fa-user me-1"></i>
                        จัดการโปรไฟล์
                      </div>

                      {isLoadingUserInfo ? (
                        <div className="bgc-7 p-3 bdr-5 text-center c-muted">
                          <i className="fas fa-spinner fa-spin me-2"></i>
                          กำลังโหลดข้อมูล...
                        </div>
                      ) : (
                        <>
                          <div className="bgc-7 p-2 bdr-5 wrap justify-content-center mb-2">
                            {userInfo?.avatar ? (
                              <img
                                src={userInfo.avatar}
                                alt=""
                                className="bdr-5"
                                style={{ width: 100, height: 100, objectFit: 'cover' }}
                              />
                            ) : (
                              <i className="fas fa-user-circle fs-100 c-red"></i>
                            )}
                          </div>

                          <div className="bgc-7 p-2 bdr-5 mb-2">
                            <label className="form-label c-dark">ชื่อผู้ใช้งาน</label>
                            <div className="bgc-4 px-2 py-1 t-medium bdr-5">
                              {userData.username}
                            </div>
                          </div>

                          <div className="bgc-7 p-2 bdr-5 mb-2">
                            <label className="form-label c-dark">ชื่อ-นามสกุล</label>
                            <div className="bgc-4 px-2 py-1 t-medium bdr-5">
                              {userData.fullName}
                            </div>
                          </div>

                          <div className="bgc-7 p-2 bdr-5 mb-2">
                            <label className="form-label c-dark">เบอร์โทร</label>
                            <div className="bgc-4 px-2 py-1 t-medium bdr-5">
                              {userData.mobile}
                            </div>
                          </div>

                          <div className="bgc-7 p-2 bdr-5 mb-2">
                            <label className="form-label c-dark">อีเมล</label>
                            <div className="bgc-4 px-2 py-1 t-medium bdr-5">
                              {userData.email}
                            </div>
                          </div>

                          <div className="bgc-7 p-2 bdr-5 mb-2">
                            <label className="form-label c-dark">วันที่สมัคร</label>
                            <div className="bgc-4 px-2 py-1 t-medium bdr-5">
                              {userData.registerAt}
                            </div>
                          </div>

                          <div className="c-dark mb-2 t-medium mt-3">
                            <i className="fas fa-university me-1"></i>
                            บัญชีธนาคาร
                          </div>
                          {activeBankAccounts.length === 0 ? (
                            <div className="bgc-7 p-3 bdr-5">
                              <p className="c-muted mb-2">ยังไม่มีบัญชีธนาคาร</p>
                              <p className="c-dark fs-14 mb-2">เพิ่มได้ครั้งเดียวเท่านั้น หลังบันทึกแล้วไม่สามารถแก้ไขได้ เพื่อความปลอดภัยและป้องกันการขโมยเงิน</p>
                              <button
                                type="button"
                                className="bgg-1 bdc-1 c-white bdr-5 px-3 py-2 border-0"
                                onClick={handleOpenAddBankModal}
                              >
                                <i className="fas fa-plus me-1"></i>
                                เพิ่มบัญชีธนาคาร
                              </button>
                            </div>
                          ) : (
                            activeBankAccounts.map((acc) => {
                              const iconUrl = getAccountIconUrl(acc)
                              const isPrimary = primaryBank?.accountNumber === acc.accountNumber
                              return (
                                <div
                                  key={acc.accountNumber + (acc.bankCode || '')}
                                  className="wrap align-items-start bgc-7 bdr-5 p-2 mb-2"
                                >
                                  <div className="img-b bdr-5 bgc-4">
                                    <img
                                      src={iconUrl}
                                      alt={acc.bankName}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement
                                        target.src = getImageUrl('icons-default-bank.png')
                                      }}
                                    />
                                  </div>
                                  <div className="txt-b ms-3 flex-grow-1">
                                    {isPrimary && (
                                      <span className="c-white bgg-4 bdr-5 px-2 py-0 mb-1 d-inline-block fs-12">
                                        บัญชีหลัก
                                      </span>
                                    )}
                                    <h6 className="c-dark mb-0">{acc.bankName}</h6>
                                    <h5 className="c-red t-medium mb-0">{acc.accountNumber}</h5>
                                    <h6 className="c-white bgg-5 bdr-5 px-2 mb-1 mt-1" style={{ width: 'fit-content' }}>
                                      ชื่อบัญชี
                                    </h6>
                                    <h5 className="c-dark t-medium fs-16 mb-0">{acc.accountName}</h5>
                                  </div>
                                </div>
                              )
                            })
                          )}

                          <div className="bgc-7 bdr-5 p-2 mt-3">
                            <p className="c-dark mb-2">ยอดเครดิตคงเหลือ</p>
                            <div className="bgg-1 p-2 bdr-5 text-center">
                              <h4 className="c-white t-regular">{userData.balance}</h4>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal เพิ่มบัญชีธนาคาร — เพิ่มได้ครั้งเดียว */}
      {showAddBankModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} onClick={() => setShowAddBankModal(false)} aria-hidden="true" />
          <div className="modal fade show" style={{ display: 'block', zIndex: 1055 }} tabIndex={-1} aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content bdr-5 shadow border-0" style={{ overflow: 'visible' }}>
                <div
                  className="modal-header border-0"
                  style={{
                    background: 'linear-gradient(180deg, #ffffff 0%, #f6f7f9 100%)',
                    borderBottom: '1px solid #e9ecef'
                  }}
                >
                  <h5 className="modal-title c-dark t-medium">
                    <i className="fas fa-university me-2"></i>
                    เพิ่มบัญชีธนาคาร
                  </h5>
                  <button type="button" className="btn-close" aria-label="ปิด" onClick={() => setShowAddBankModal(false)} />
                </div>
                <div className="modal-body" style={{ background: '#ffffff', overflow: 'visible' }}>
                  <p className="c-dark small mb-3" style={{ lineHeight: 1.6 }}>
                    บันทึกได้เพียงครั้งเดียว ไม่สามารถแก้ไขภายหลัง เพื่อความปลอดภัยและป้องกันการขโมยเงิน
                  </p>
                  <div className="mb-2">
                    <label className="form-label c-dark t-medium">
                      ธนาคาร
                      <span className="ms-2 text-muted fw-normal">({availableBanks.length} รายการที่เปิดใช้งาน)</span>
                    </label>
                    <div
                      ref={bankDropdownRef}
                      className="position-relative"
                    >
                      <button
                        type="button"
                        className="form-control d-flex align-items-center justify-content-between text-start"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderColor: '#d7dce1',
                          minHeight: 42,
                          color: editBankCode ? '#212529' : '#6c757d'
                        }}
                        disabled={isLoadingBankIcons || availableBanks.length === 0}
                        onClick={() => setShowBankDropdown((open) => !open)}
                      >
                        <span className="d-flex align-items-center gap-2">
                          {selectedBank?.imageUrl && (
                            <img
                              src={selectedBank.imageUrl}
                              alt={selectedBank.name}
                              style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                target.src = getImageUrl('icons-default-bank.png')
                              }}
                            />
                          )}
                          <span>
                            {isLoadingBankIcons
                              ? 'กำลังโหลดธนาคาร...'
                              : availableBanks.length === 0
                                ? 'ไม่พบธนาคารที่เปิดใช้งาน'
                                : selectedBank?.name || 'เลือกธนาคาร'}
                          </span>
                        </span>
                        <i className={`fas fa-chevron-${showBankDropdown ? 'up' : 'down'}`}></i>
                      </button>

                      {showBankDropdown && availableBanks.length > 0 && (
                        <div
                          className="position-absolute start-0 end-0 mt-1 bdr-5 shadow-sm"
                          style={{
                            zIndex: 30,
                            background: '#ffffff',
                            border: '1px solid #d7dce1',
                            maxHeight: 360,
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            WebkitOverflowScrolling: 'touch'
                          }}
                        >
                          {availableBanks.map((bank) => (
                            <button
                              key={bank.code}
                              type="button"
                              className="w-100 text-start border-0 d-flex align-items-center gap-2"
                              style={{
                                padding: '10px 12px',
                                background: bank.code === editBankCode ? '#e8f7f1' : '#ffffff',
                                color: '#212529'
                              }}
                              onClick={() => {
                                setEditBankCode(bank.code)
                                setEditBankName(bank.name)
                                setShowBankDropdown(false)
                              }}
                            >
                              <img
                                src={bank.imageUrl}
                                alt={bank.name}
                                style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.src = getImageUrl('icons-default-bank.png')
                                }}
                              />
                              <span>{bank.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label c-dark t-medium">หมายเลขบัญชี (10–12 หลัก)</label>
                    <input
                      type="text"
                      className="form-control"
                      style={{ backgroundColor: '#f8f9fa', borderColor: '#d7dce1' }}
                      placeholder="เฉพาะตัวเลข"
                      value={editAccountNumber}
                      onChange={(e) => setEditAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label c-dark t-medium">ชื่อบัญชี</label>
                    <input
                      type="text"
                      className="form-control"
                      style={{ backgroundColor: '#f8f9fa', borderColor: '#d7dce1' }}
                      placeholder="ชื่อบนบัญชีธนาคาร"
                      value={editAccountName}
                      onChange={(e) => setEditAccountName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer border-0" style={{ background: '#ffffff', borderTop: '1px solid #eef1f4' }}>
                  <button type="button" className="btn btn-light bdr-5" style={{ borderColor: '#d7dce1', color: '#495057' }} onClick={() => setShowAddBankModal(false)}>
                    ปิด
                  </button>
                  <button
                    type="button"
                    className="btn c-white bdr-5 border-0"
                    style={{ background: 'linear-gradient(180deg, #19a974 0%, #128c60 100%)' }}
                    disabled={addBankSubmitting}
                    onClick={handleSubmitAddBank}
                  >
                    {addBankSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ProfilePage
